import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { 
  ChevronDown, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight, 
  Truck, 
  Calendar, 
  MapPin, 
  Zap, 
  Users, 
  UserCheck, 
  BarChart3, 
  History, 
  Building2, 
  Hospital, 
  Mail, 
  Percent, 
  Settings, 
  User, 
  FileText,
  Sliders,
  ShieldCheck,
  Headphones,
  LogOut,
  LifeBuoy,
  ClipboardList,
  Radio,
  UserPlus,
  DollarSign,
  LayoutDashboard,
  Tag,
  Car,
  Shield,
  FileCheck,
  SlidersHorizontal
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

import DashboardIcon from '../svg/Dashboard icon.svg';
import RevenueIcon from '../svg/Revenue icon.svg';
import OperationIcon from '../svg/Operations icon.svg';
import AnalyticsIcon from '../svg/Analytics icon.svg';
import HospitalsIcon from '../svg/Hospitals icon.svg';
import OperatorIcon from '../svg/Operators icon.svg';
import DriverIcon from '../svg/Drivers icon.svg';
import VehicleIcon from '../svg/Vehicles icon.svg';
import UserIcon from '../svg/User icon.svg';
import SettingIcon from '../svg/Setting icon.svg';
import ProfileIcon from '../svg/Profile icon.svg';
import PercentIcon from '../svg/Percent icon.svg';
import GroupSvg from '../svg/Group.svg';

// Default grouped sections with unique semantic icons
const defaultSections = [
  {
    title: 'BOOKING & TRACKING',
    items: [
      { id: 'tracking', icon: MapPin, label: 'Trips' },
      { id: 'booking', icon: Calendar, label: 'Booking' },
      { id: 'instant_booking', icon: Zap, label: 'Booking' },
      { id: 'instant-booking-duplicate', icon: Zap, label: 'Instant Booking' },
      { id: 'book_ambulance', icon: Truck, label: 'Book Ambulance' },
    ]
  },
  {
    title: 'OPERATIONS',
    items: [
      { id: 'assignments', icon: ClipboardList, label: 'Assignments' },
      { id: 'driver-availability', icon: UserCheck, label: 'Driver Availability' },
      { id: 'drivers', icon: Users, label: 'Drivers & Vehicles' },
      { id: 'driver_vehicle_requests', icon: UserCheck, label: 'Driver/Vehicle Requests' },
      { id: 'dispatch', icon: Radio, label: 'Dispatch (static)' },
      { id: 'test', icon: Car, label: 'All Vehicles' },
      { id: 'vehicles', icon: Car, label: 'Vehicles' },
      { id: 'all_vehicles', icon: Car, label: 'All Vehicles' },
      { id: 'operators', icon: UserPlus, label: 'Operators' },
      { id: 'fleet_config', icon: Sliders, label: 'Fleet Config' }
    ]
  },
  {
    title: 'HOSPITALS & PARTNERS',
    items: [
      { id: 'partners', icon: Hospital, label: 'Partner Hospitals' },
      { id: 'hospitals', icon: Building2, label: 'Hospital' },
      { id: 'onboarding_approvals', icon: FileCheck, label: 'Onboarding Approvals' },
      { id: 'hospital_approvals', icon: Hospital, label: 'Hospital Approvals' },
      { id: 'fleet_approvals', icon: ShieldCheck, label: 'Fleet Approvals' },
      { id: 'hospital_requests', icon: FileText, label: 'Hospital Requests' },
      { id: 'invitations', icon: Mail, label: 'Invitations' }
    ]
  },
  {
    title: 'REPORTS & ANALYTICS',
    items: [
      { id: 'analytics', icon: BarChart3, label: 'Analytics (static)' },
      { id: 'reports', icon: FileText, label: 'Reports (static)' },
      { id: 'history', icon: History, label: 'History' },
      { id: 'revenue', icon: DollarSign, label: 'Revenue' },
      { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' }
    ]
  },
  {
    title: 'PRICING & SETTINGS',
    items: [
      { id: 'super_admin_master', icon: Shield, label: 'Super Admin Master' },
      { id: 'adv_pricing_override', icon: Percent, label: 'Margin & Discount' },
      { id: 'master_adjustment', icon: Sliders, label: 'Master Adjustment' },
      { id: 'adjustment_request', icon: FileCheck, label: 'Adjustment Request' },
      { id: 'adv_master_pricing', icon: Percent, label: 'Adv. Master Pricing' },
      { id: 'booking_form_options', icon: SlidersHorizontal, label: 'Booking Master Fields' },
      { id: 'my-pricing', icon: Tag, label: 'Pricing Config' },
    ]
  },
  {
    title: 'SUPPORT & ACCOUNT',
    items: [
      { id: 'raise_ticket', icon: LifeBuoy, label: 'Raise a Ticket' },
      { id: 'support_tickets', icon: LifeBuoy, label: 'Support Tickets' },
      { id: 'support', icon: Headphones, label: 'Support' },
      { id: 'settings', icon: Settings, label: 'Settings' },
      { id: 'profile', icon: User, label: 'Profile' }
    ]
  }
];

const Sidebar = ({ 
  sections: externalSections,
  menuItems,
  onItemClick,
  selectedItem: externalSelectedItem,
  defaultSelectedItem = 'booking',
  className = '',
  isMobileMenuOpen = false,
  onMobileMenuToggle = null,
  ...props 
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false);
  const [internalSelectedItem, setInternalSelectedItem] = useState(defaultSelectedItem);
  
  // Dynamic Tooltip State for Collapsed Mode (prevents overflow-y-auto clipping)
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  
  // Safe Auth Context integration
  let authContext = null;
  try {
    authContext = useAuth();
  } catch (e) {
    authContext = null;
  }
  const user = authContext?.user;
  const logout = authContext?.logout;

  // Track open/collapsed section accordions (all open by default)
  const [openSections, setOpenSections] = useState({});

  const isMobileMenuOpenState = isMobileMenuOpen !== undefined ? isMobileMenuOpen : internalMobileMenuOpen;
  const setIsMobileMenuOpenState = onMobileMenuToggle || setInternalMobileMenuOpen;
  const selectedItem = externalSelectedItem !== undefined ? externalSelectedItem : internalSelectedItem;

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Build the list of sections based on externalSections or menuItems
  const formattedSections = useMemo(() => {
    if (externalSections && externalSections.length > 0) {
      return externalSections;
    }

    if (!menuItems || menuItems.length === 0) {
      return defaultSections;
    }

    // Map passed menuItems into section categories dynamically
    const itemMap = new Map(menuItems.map(item => [item.id, item]));
    const usedIds = new Set();

    const sections = defaultSections.map(sec => {
      const sectionItems = [];
      sec.items.forEach(secItem => {
        if (itemMap.has(secItem.id)) {
          sectionItems.push(itemMap.get(secItem.id));
          usedIds.add(secItem.id);
        }
      });
      return {
        title: sec.title,
        items: sectionItems
      };
    }).filter(sec => sec.items.length > 0);

    // Remaining items not matched in preset categories
    const unmappedItems = menuItems.filter(item => !usedIds.has(item.id));
    if (unmappedItems.length > 0) {
      sections.push({
        title: 'OTHER',
        items: unmappedItems
      });
    }

    return sections;
  }, [externalSections, menuItems]);

  // Ensure section containing selected item is expanded
  useEffect(() => {
    const newOpenState = { ...openSections };
    let updated = false;

    formattedSections.forEach((sec) => {
      const hasActive = sec.items.some(item => item.id === selectedItem);
      if (hasActive && newOpenState[sec.title] === undefined) {
        newOpenState[sec.title] = true;
        updated = true;
      } else if (newOpenState[sec.title] === undefined) {
        newOpenState[sec.title] = true; // Default open all sections for quick discovery
        updated = true;
      }
    });

    if (updated) {
      setOpenSections(newOpenState);
    }
  }, [selectedItem, formattedSections]);

  const toggleSection = (title) => {
    setOpenSections(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const handleItemClick = (itemId) => {
    if (onItemClick) {
      onItemClick(itemId);
    } else {
      setInternalSelectedItem(itemId);
    }
    if (isMobile) {
      setIsMobileMenuOpenState(false);
    }
  };

  const handleLogout = async () => {
    if (logout) {
      try {
        await logout();
        window.location.href = '/login';
      } catch (err) {
        console.error('Logout failed:', err);
        window.location.href = '/login';
      }
    }
  };

  // Tooltip Mouse Handlers
  const handleMouseEnterTooltip = (e, label) => {
    if (isCollapsed) {
      const rect = e.currentTarget.getBoundingClientRect();
      setHoveredTooltip({
        label,
        top: rect.top + rect.height / 2
      });
    }
  };

  const handleMouseLeaveTooltip = () => {
    setHoveredTooltip(null);
  };

  // Helper to render icon smoothly
  const renderItemIcon = (icon, isActive) => {
    if (!icon) return null;

    if (typeof icon === 'function' || (typeof icon === 'object' && icon.$$typeof)) {
      const IconComp = icon;
      return <IconComp className={`w-5 h-5 shrink-0 transition-colors ${isActive ? 'text-blue-600' : 'text-slate-500 group-hover:text-slate-700'}`} />;
    }

    if (typeof icon === 'string') {
      return (
        <img 
          src={icon} 
          alt="" 
          className={`w-5 h-5 shrink-0 transition-all duration-200 ${
            isActive ? 'brightness-[0.2] invert-[0.35] sepia-[1] saturate-[5] hue-rotate-[210deg]' : 'opacity-70 group-hover:opacity-100'
          }`}
          style={{
            filter: isActive ? 'invert(27%) sepia(95%) saturate(2250%) hue-rotate(215deg) brightness(96%) contrast(97%)' : undefined
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isMobile && isMobileMenuOpenState && (
        <div 
          className="lg:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-40 transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpenState(false)}
        />
      )}

      {/* Sidebar Main Container */}
      <aside
        className={`h-full z-50 bg-white border-r border-slate-200/80 shadow-sm flex flex-col transition-all duration-300 ease-in-out select-none ${className} ${
          isMobile 
            ? (isMobileMenuOpenState 
                ? 'fixed top-0 left-0 w-64 translate-x-0' 
                : 'fixed top-0 -left-72 w-64 -translate-x-full')
            : (isCollapsed ? 'w-20 relative' : 'w-64 relative')
        }`}
        {...props}
      >
        {/* Interactive Brand Header: Click Entire Logo + Title Header to Collapse/Expand */}
        {!isCollapsed ? (
          /* Expanded Header with Full RISA Brand Logo and Permanently Visible Collapse Toggle */
          <div 
            className="relative flex items-center justify-between px-4 py-3.5 border-b border-slate-100/90 shrink-0 select-none h-[73px]"
          >
            <div 
              onClick={() => !isMobile && setIsCollapsed(true)}
              className="flex items-center gap-3 overflow-hidden cursor-pointer group"
              title="Click logo to collapse sidebar"
            >
              <img 
                src={GroupSvg} 
                alt="RISA Smart Ambulance Logo" 
                className="h-10 w-auto max-w-[170px] object-contain transition-transform group-hover:scale-102" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/logo.svg';
                }} 
              />
            </div>

            {/* Inward Collapse Arrow Button - Permanently Visible */}
            <button
              type="button"
              onClick={() => !isMobile && setIsCollapsed(true)}
              title="Collapse sidebar"
              className="w-8 h-8 rounded-lg border border-slate-200/90 bg-slate-50/70 hover:bg-blue-50 hover:border-blue-200 flex items-center justify-center text-slate-500 hover:text-blue-600 transition-all shrink-0 cursor-pointer shadow-2xs"
            >
              <ChevronsLeft className="w-4.5 h-4.5" />
            </button>
          </div>
        ) : (
          /* Collapsed Clickable Header with Crisp Icon Emblem Badge & Permanently Visible Expand Toggle */
          <div 
            className="relative flex items-center justify-center h-[73px] border-b border-slate-100/90 shrink-0 select-none px-2"
          >
            <div 
              onClick={() => !isMobile && setIsCollapsed(false)}
              onMouseEnter={(e) => handleMouseEnterTooltip(e, 'Click to expand sidebar')}
              onMouseLeave={handleMouseLeaveTooltip}
              className="w-10 h-10 rounded-xl bg-white border border-slate-200/90 shadow-2xs flex items-center justify-center p-2 cursor-pointer transition-all hover:scale-105 hover:border-blue-300 hover:shadow-xs group"
              title="Click emblem to expand sidebar"
            >
              <img 
                src="/favicon.svg" 
                alt="RISA Emblem" 
                className="w-full h-full object-contain" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = GroupSvg;
                }} 
              />
            </div>

            {/* Outward Expand Arrow Button - Permanently Visible on Border */}
            <button
              type="button"
              onClick={() => !isMobile && setIsCollapsed(false)}
              title="Expand sidebar"
              className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-slate-200/90 shadow-xs flex items-center justify-center text-slate-500 hover:text-blue-600 hover:border-blue-300 hover:scale-110 transition-all cursor-pointer z-20"
            >
              <ChevronsRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Scrollable Navigation Sections */}
        <div className="flex-1 overflow-y-auto py-3 px-3 scrollbar-hide space-y-4">
          {formattedSections.map((section, idx) => {
            const isSectionOpen = openSections[section.title] !== false;
            const hasActiveItem = section.items.some(item => item.id === selectedItem);

            return (
              <div key={section.title || idx} className="space-y-1">
                {/* Section Category Header */}
                {!isCollapsed ? (
                  <button
                    onClick={() => toggleSection(section.title)}
                    className="w-full flex items-center justify-between px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors group cursor-pointer"
                  >
                    <span className={hasActiveItem ? 'text-blue-600 font-bold' : ''}>
                      {section.title}
                    </span>
                    <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                      {isSectionOpen ? (
                        <ChevronDown className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5" />
                      )}
                    </span>
                  </button>
                ) : (
                  /* Collapsed Divider Line */
                  idx > 0 && <div className="border-t border-slate-100 my-2 mx-1" />
                )}

                {/* Sub-menu Items List */}
                {(isSectionOpen || isCollapsed) && (
                  <div className="space-y-1 transition-all duration-200">
                    {section.items.map((item) => {
                      const isActive = selectedItem === item.id;

                      return (
                        <div key={item.id} className="relative group flex justify-center">
                          <button
                            onClick={() => handleItemClick(item.id)}
                            onMouseEnter={(e) => handleMouseEnterTooltip(e, item.label)}
                            onMouseLeave={handleMouseLeaveTooltip}
                            className={
                              isCollapsed
                                ? `w-11 h-11 mx-auto flex items-center justify-center rounded-xl transition-all duration-200 cursor-pointer ${
                                    isActive
                                      ? 'bg-blue-50/90 text-blue-600 font-bold shadow-xs border-2 border-blue-500/30'
                                      : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900'
                                  }`
                                : `relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 cursor-pointer ${
                                    isActive
                                      ? 'bg-blue-50/90 text-blue-700 font-semibold shadow-xs border-l-4 border-blue-600 pl-2.5'
                                      : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900 font-medium pl-3'
                                  }`
                            }
                          >
                            {renderItemIcon(item.icon, isActive)}

                            {!isCollapsed && (
                              <span className="truncate leading-tight text-left">
                                {item.label}
                              </span>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Sidebar Bottom Footer Section */}
        <div className="p-3 border-t border-slate-100 bg-slate-50/40 space-y-2 shrink-0">
          {!isCollapsed ? (
            <>
              {/* 24/7 Support Helpline Card - Temporarily commented out as requested */}
              {/* 
              <div className="bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border border-blue-100/80 rounded-xl p-2.5 flex items-center justify-between shadow-xs">
                <div className="flex items-center gap-2.5 overflow-hidden">
                  <div className="w-8 h-8 rounded-lg bg-blue-600/10 text-blue-600 flex items-center justify-center shrink-0">
                    <Headphones className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold text-slate-800 leading-tight truncate">
                      24/7 Support
                    </span>
                    <span className="text-[10px] text-blue-600 font-semibold truncate">
                      1800-108-1800
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                </div>
              </div>
              */}

              {/* User Profile & Logout Card */}
              <div className="bg-white border border-slate-200/80 rounded-xl p-2.5 flex items-center justify-between shadow-xs hover:border-slate-300 transition-colors">
                <div className="flex items-center gap-2.5 overflow-hidden">
                  <div className="w-8.5 h-8.5 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-white font-bold text-xs flex items-center justify-center shadow-xs shrink-0">
                    {user?.name ? user.name.slice(0, 2).toUpperCase() : (user?.email ? user.email.slice(0, 2).toUpperCase() : 'FA')}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold text-slate-900 truncate leading-tight">
                      {user?.name || user?.email?.split('@')[0] || 'Fleet Partner'}
                    </span>
                    <span className="text-[10px] text-slate-500 font-medium capitalize truncate">
                      {user?.role || 'Fleet Operator'}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Collapsed Compact View Footer with Anchored Hover Tooltips */
            <div className="flex flex-col items-center gap-2.5 py-1">
              {/* Support Icon Button - Temporarily commented out as requested */}
              {/* 
              <button 
                onMouseEnter={(e) => handleMouseEnterTooltip(e, '24/7 Helpline: 1800-108-1800')}
                onMouseLeave={handleMouseLeaveTooltip}
                className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100 transition-colors cursor-pointer shrink-0"
              >
                <Headphones className="w-4 h-4" />
              </button>
              */}

              {/* User Profile Avatar */}
              <div 
                onMouseEnter={(e) => handleMouseEnterTooltip(e, `${user?.name || user?.email || 'Fleet Partner'} (${user?.role || 'Fleet Operator'})`)}
                onMouseLeave={handleMouseLeaveTooltip}
                className="w-9 h-9 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-white font-bold text-xs flex items-center justify-center shadow-xs cursor-pointer shrink-0"
              >
                {user?.name ? user.name.slice(0, 2).toUpperCase() : 'FA'}
              </div>
            </div>
          )}
        </div>

        {/* Global Viewport-Fixed Hover Tooltip Popup (Never Clipped by Overflow) */}
        {isCollapsed && hoveredTooltip && (
          <div 
            className="fixed left-[88px] z-[9999] px-3.5 py-1.5 bg-slate-900 text-white text-xs font-semibold rounded-lg shadow-2xl border border-slate-800 whitespace-nowrap pointer-events-none flex items-center gap-2 -translate-y-1/2 transition-all duration-150 animate-in fade-in zoom-in-95"
            style={{ top: `${hoveredTooltip.top}px` }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
            <span>{hoveredTooltip.label}</span>
            <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-900 rotate-45 border-l border-b border-slate-800" />
          </div>
        )}
      </aside>

      {/* Hide Scrollbar Style */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

Sidebar.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.any,
      label: PropTypes.string.isRequired
    }))
  })),
  menuItems: PropTypes.array,
  onItemClick: PropTypes.func,
  selectedItem: PropTypes.string,
  defaultSelectedItem: PropTypes.string,
  className: PropTypes.string,
  isMobileMenuOpen: PropTypes.bool,
  onMobileMenuToggle: PropTypes.func
};

export default Sidebar;
